import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, CalendarDays, Flag, Sparkles, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { StatusBadge, type ProjectStatus } from "@/components/StatusBadge";

export const Route = createFileRoute("/_authenticated/calendar")({
  head: () => ({
    meta: [
      { title: "Calendar — Lumvio" },
      {
        name: "description",
        content:
          "Your Lumvio project calendar: every deadline, review window, and delivery date in one clean monthly view.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CalendarPage,
});

type CalProject = {
  id: string;
  title: string;
  status: ProjectStatus;
  deadline: string | null;
  start_date: string | null;
  completed_at: string | null;
  client_email: string;
  freelancer_id: string;
};

type EventKind = "deadline" | "start" | "completed";
type CalEvent = {
  id: string;
  project: CalProject;
  kind: EventKind;
  date: Date;
};

const MS_DAY = 86400000;

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function ymdKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function CalendarPage() {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const isFreelancer = profile?.role === "freelancer";
  const isAr = (i18n.language || "en").startsWith("ar");
  const locale = isAr ? "ar" : "en-US";

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<Date>(new Date());

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["calendar-projects", user?.id, profile?.role, profile?.email],
    enabled: !!user && !!profile,
    queryFn: async () => {
      let q = supabase
        .from("projects")
        .select("id,title,status,deadline,start_date,completed_at,client_email,freelancer_id")
        .eq("is_intro", false);
      if (isFreelancer) q = q.eq("freelancer_id", user!.id);
      else q = q.ilike("client_email", profile!.email ?? "");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CalProject[];
    },
  });

  // Build events across ~6 months (prev/next) for smooth navigation
  const events = useMemo<CalEvent[]>(() => {
    const evs: CalEvent[] = [];
    for (const p of projects) {
      if (p.deadline) evs.push({ id: `${p.id}-d`, project: p, kind: "deadline", date: new Date(p.deadline) });
      if (p.start_date) evs.push({ id: `${p.id}-s`, project: p, kind: "start", date: new Date(p.start_date) });
      if (p.completed_at) evs.push({ id: `${p.id}-c`, project: p, kind: "completed", date: new Date(p.completed_at) });
    }
    return evs;
  }, [projects]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalEvent[]>();
    for (const e of events) {
      const k = ymdKey(e.date);
      const arr = map.get(k) ?? [];
      arr.push(e);
      map.set(k, arr);
    }
    return map;
  }, [events]);

  const first = startOfMonth(cursor);
  const last = endOfMonth(cursor);
  // Week starts on Sunday; adjust as needed
  const leading = first.getDay();
  const totalCells = Math.ceil((leading + last.getDate()) / 7) * 7;
  const days: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    days.push(new Date(first.getTime() + (i - leading) * MS_DAY));
  }

  const today = new Date();
  const monthLabel = cursor.toLocaleDateString(locale, { month: "long", year: "numeric" });

  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const weekdays = Array.from({ length: 7 }, (_, i) => weekdayFmt.format(new Date(2024, 8, 1 + i))); // Sun..Sat

  const selectedEvents = eventsByDay.get(ymdKey(selected)) ?? [];

  // Upcoming (next 30 days) across all projects
  const upcoming = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + 30 * MS_DAY);
    return events
      .filter((e) => e.date >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && e.date <= horizon)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 8);
  }, [events]);

  const monthStats = useMemo(() => {
    const inMonth = events.filter((e) => e.date >= first && e.date <= last);
    return {
      total: inMonth.length,
      deadlines: inMonth.filter((e) => e.kind === "deadline").length,
      completed: inMonth.filter((e) => e.kind === "completed").length,
    };
  }, [events, first, last]);

  return (
    <div className="relative mx-auto max-w-6xl space-y-6 px-4 py-6 sm:py-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary/10 via-background to-background p-6 sm:p-8">
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-primary/8 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              <CalendarDays className="h-3 w-3" /> {t("calendarPage.badge")}
            </div>
            <h1 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold tracking-tight">
              {t("calendarPage.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("calendarPage.subtitle")}</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <MiniStat icon={CalendarDays} label={t("calendarPage.stats.events")} value={monthStats.total} />
            <MiniStat icon={Flag} label={t("calendarPage.stats.deadlines")} value={monthStats.deadlines} tone="amber" />
            <MiniStat icon={CheckCircle2} label={t("calendarPage.stats.completed")} value={monthStats.completed} tone="emerald" />
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Month grid */}
        <section className="rounded-2xl border border-border/60 bg-card/60 p-4 sm:p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          {/* Header controls */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label={t("calendarPage.prev")}
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-background/60 text-muted-foreground transition hover:text-foreground hover:border-border"
              >
                <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
              </button>
              <button
                type="button"
                aria-label={t("calendarPage.next")}
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-background/60 text-muted-foreground transition hover:text-foreground hover:border-border"
              >
                <ChevronRight className="h-4 w-4 rtl:rotate-180" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const now = new Date();
                  setCursor(startOfMonth(now));
                  setSelected(now);
                }}
                className="ms-1 rounded-lg border border-border/60 bg-background/60 px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground hover:border-border"
              >
                {t("calendarPage.today")}
              </button>
            </div>
            <h2 className="font-serif text-base sm:text-lg font-semibold tracking-tight capitalize">
              {monthLabel}
            </h2>
          </div>

          {/* Weekday header */}
          <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {weekdays.map((w, i) => (
              <div key={i} className="py-1.5">{w}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const isSel = sameDay(d, selected);
              const dayEvents = eventsByDay.get(ymdKey(d)) ?? [];
              const has = dayEvents.length > 0;
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  onClick={() => setSelected(d)}
                  className={cn(
                    "group relative flex aspect-square flex-col items-stretch justify-between rounded-xl border p-1.5 text-left transition-all sm:aspect-[1/0.9]",
                    inMonth
                      ? "border-border/50 bg-background/40 hover:border-primary/40 hover:bg-primary/5"
                      : "border-transparent bg-transparent text-muted-foreground/40 hover:bg-muted/20",
                    isSel && "border-primary/60 bg-primary/10 shadow-[0_0_0_3px_color-mix(in_oklch,var(--primary)_15%,transparent)]",
                    isToday && !isSel && "border-primary/40",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                      isToday
                        ? "bg-primary text-primary-foreground shadow-[0_0_10px_color-mix(in_oklch,var(--primary)_45%,transparent)]"
                        : isSel
                          ? "text-primary"
                          : "text-foreground/80",
                    )}
                  >
                    {d.getDate()}
                  </span>
                  {has && (
                    <div className="flex flex-wrap items-end gap-0.5">
                      {dayEvents.slice(0, 3).map((e) => (
                        <span
                          key={e.id}
                          className={cn(
                            "h-1.5 w-1.5 rounded-full",
                            e.kind === "deadline" && "bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.6)]",
                            e.kind === "completed" && "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.55)]",
                            e.kind === "start" && "bg-primary shadow-[0_0_6px_color-mix(in_oklch,var(--primary)_60%,transparent)]",
                          )}
                        />
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="text-[9px] font-semibold text-muted-foreground">+{dayEvents.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            <LegendDot color="bg-primary" label={t("calendarPage.legend.start")} />
            <LegendDot color="bg-amber-500" label={t("calendarPage.legend.deadline")} />
            <LegendDot color="bg-emerald-500" label={t("calendarPage.legend.completed")} />
          </div>
        </section>

        {/* Side panel */}
        <aside className="space-y-4">
          {/* Selected day */}
          <div className="rounded-2xl border border-border/60 bg-card/60 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("calendarPage.selectedDay")}
                </div>
                <div className="mt-0.5 font-serif text-lg font-semibold tracking-tight">
                  {selected.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
                </div>
              </div>
              <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-primary/10 px-2 text-sm font-semibold text-primary tabular-nums">
                {selectedEvents.length}
              </span>
            </div>
            <div className="mt-3 space-y-2">
              {selectedEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("calendarPage.empty.day")}</p>
              ) : (
                selectedEvents.map((e) => <EventRow key={e.id} event={e} />)
              )}
            </div>
          </div>

          {/* Upcoming 30 days */}
          <div className="rounded-2xl border border-border/60 bg-card/60 p-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t("calendarPage.upcoming")}
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {isLoading ? (
                <div className="h-16 animate-pulse rounded-lg bg-muted/40" />
              ) : upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("calendarPage.empty.upcoming")}</p>
              ) : (
                upcoming.map((e) => <EventRow key={e.id} event={e} showDate />)
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
  tone = "primary",
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  tone?: "primary" | "amber" | "emerald";
}) {
  const cls =
    tone === "amber"
      ? "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"
      : tone === "emerald"
        ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
        : "text-primary bg-primary/10 border-primary/20";
  return (
    <div className={cn("flex items-center gap-2.5 rounded-xl border px-3 py-2", cls)}>
      <Icon className="h-4 w-4" />
      <div className="leading-tight">
        <div className="text-lg font-semibold tabular-nums">{value}</div>
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-2 w-2 rounded-full", color)} />
      {label}
    </span>
  );
}

function EventRow({ event, showDate = false }: { event: CalEvent; showDate?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "en").startsWith("ar") ? "ar" : "en-US";
  const Icon = event.kind === "deadline" ? Flag : event.kind === "completed" ? CheckCircle2 : Clock;
  const kindLabel = t(`calendarPage.kind.${event.kind}`);
  const now = new Date();
  const overdue = event.kind === "deadline" && event.date < now && event.project.status !== "completed";
  return (
    <Link
      to="/projects/$id"
      params={{ id: event.project.id }}
      className="group flex items-start gap-2.5 rounded-xl border border-border/50 bg-background/40 p-2.5 transition-all hover:-translate-y-0.5 hover:border-primary/40"
    >
      <span
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-lg border",
          event.kind === "deadline" && "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
          event.kind === "completed" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
          event.kind === "start" && "border-primary/30 bg-primary/10 text-primary",
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium group-hover:text-primary">{event.project.title}</span>
          {overdue && <AlertCircle className="h-3 w-3 text-destructive" />}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span>{kindLabel}</span>
          {showDate && (
            <>
              <span>·</span>
              <span className="tabular-nums">
                {event.date.toLocaleDateString(locale, { day: "numeric", month: "short" })}
              </span>
            </>
          )}
          <span>·</span>
          <StatusBadge status={event.project.status} className="scale-90" />
        </div>
      </div>
    </Link>
  );
}
